import { enqueueSnackbar } from 'notistack';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';
import axios from 'axios';
import type {
  AddApplicantMessageBody,
  AddReactionMessageBody,
  ApplicantListParam,
  ChatMessageBody,
  CreateChatGroupBody,
  CreateChatTemplateBody,
  JobInviteRejectBody,
  GetOrganizationUsersListResponse,
  UpdateChatGroupBody,
  AdminInitiateSupportBody,
  UpdateChatMessageBody,
  BulkTemplateSupportBody,
  JobPostedSub,
} from 'src/openapi';
import type { ChatSenderType } from 'src/pages/chat/types';
import ConfigUrl from '../../../config';
// import { getFilenameFromUrl } from 'pdfjs-dist';

export const fetchApplicantChatJobs = createAsyncThunk(
  `fetch/applicant-chat-jobs`,
  async (
    {
      query,
      pageNo,
      pageSize,
    }: {
      query: string;
      pageNo?: number;
      pageSize?: number;
      shouldSelectFirst?: boolean;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetChatUsersForApplicant(
        { page: pageNo, pageSize, query },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

let jobChatsAbortController: AbortController | null = null;
export const fetchChats = createAsyncThunk(
  'fetch/user-chats',
  async (
    {
      jobId,
      applicantUserId,
      cursor,
      pageSize = 25,
    }: {
      jobId: string;
      applicantUserId: string;
      senderType: ChatSenderType;
      cursor?: string;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      if (jobChatsAbortController) {
        jobChatsAbortController.abort();
      }
      jobChatsAbortController = new AbortController();
      const res = await (
        await apiClient.getJobApi()
      ).jobApiGetJobApplicantMessage(
        { applicantId: applicantUserId, jobId, cursor, pageSize },
        { signal: jobChatsAbortController.signal },
      );
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendMessage = createAsyncThunk(
  `post/send-message`,
  async (
    {
      jobId,
      applicantUserId,
      message,
      attachmentURL,
    }: {
      jobId: string;
      applicantUserId: string;
      message: string;
      attachmentURL?: string;
      senderType: ChatSenderType;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAddJobApplicantMsg({
        jobId,
        addApplicantMessageBody: {
          message,
          applicant_id: applicantUserId,
          attachment_url: attachmentURL,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadChatAttachment = createAsyncThunk(
  'upload/chat-attachment',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getFilesApi()
      ).filesApiUploadJobChatAttachment({ file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchChatApplicants = createAsyncThunk(
  'fetch/chat-applicants',
  async (
    {
      jobId,
      orgId,
      filters = {},
    }: {
      jobId: string;
      orgId: string;
      filters?: ApplicantListParam;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobApplicantsList({
        jobId,
        orgId,
        applicantListParam: filters,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

// Org-wide applicant chat inbox — every conversation across all of the org's
// jobs, for the chat landing view where no single job is selected.
// Each call aborts the previous in-flight one so a superseded fetch (e.g. an
// old search term, or page 2 of a now-stale query) can't resolve and append
// onto a fresh result set. The reducer additionally drops any stale-query
// payload that slips through the abort window.
let orgApplicantChatsAbortController: AbortController | null = null;
export const fetchOrgApplicantChats = createAsyncThunk(
  'fetch/org-applicant-chats',
  async (
    {
      orgId,
      query,
      jobRoleId,
      pageNo = 1,
      pageSize = 20,
    }: {
      orgId: string;
      query?: string;
      jobRoleId?: string;
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      if (orgApplicantChatsAbortController) {
        orgApplicantChatsAbortController.abort();
      }
      orgApplicantChatsAbortController = new AbortController();
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetOrgApplicantChats(
        {
          orgId,
          jobRoleId,
          page: pageNo,
          pageSize,
          query,
        },
        { signal: orgApplicantChatsAbortController.signal },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err?.response?.data);
    }
  },
);

// Distinct jobs across the org that have at least one conversation, newest
// first — powers the Job Role dropdown so it lists every job with messages
// (not capped by the inbox page size) and never one without.
// TODO(update-spec): move onto the typed client once `GET /org/:id/chat-jobs`
// ships and `npm run update-spec` regenerates it.
export const fetchOrgChatJobs = createAsyncThunk(
  'fetch/org-chat-jobs',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await axios.get<{ jobs: JobPostedSub[] }>(
        `${ConfigUrl.API_URL}/org/${orgId}/chat-jobs`,
        {
          headers: { Authorization: `Bearer ${apiClient.apiKey}` },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const fetchApplicantDetails = createAsyncThunk(
  'fetch/applicant-details',
  async (
    {
      jobId,
      orgId,
      applicantId,
    }: {
      jobId: string;
      orgId: string;
      applicantId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobApplicantsDetails({ applicantId, jobId, orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

let postedJobsAbortController: AbortController | null = null;
export const fetchEmployerPostedJobs = createAsyncThunk(
  'org/fetch-employer-posted-jobs',
  async (
    {
      query,
      pageNo,
      pageSize,
      orgId,
    }: { query?: string; pageNo: number; pageSize: number; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      if (postedJobsAbortController) {
        postedJobsAbortController.abort();
      }

      postedJobsAbortController = new AbortController();
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobPostedList(
        {
          isArchieved: undefined,
          isCompetitive: undefined,
          isGptJob: false,
          jobStatus: 'Active,Closed',
          orgId,
          pageNo,
          pageSize,
          planTypeList: undefined,
          productType: undefined,
          query,
        },
        { signal: postedJobsAbortController.signal },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchEmployerChatTemplateList = createAsyncThunk(
  'fetch/employer-all-chat-templates',
  async (
    {
      orgId,
      pageNo,
      pageSize,
      query,
    }: { orgId: string; pageNo: number; pageSize: number; query?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiGetChatTemplateList({
        orgId,
        page: pageNo,
        pageSize,
        query,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const createEmployerChatTemplate = createAsyncThunk(
  'post/employer-chat-template',
  async (
    { orgId, data }: { orgId: string; data: CreateChatTemplateBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiCreateChatTemplate({
        orgId,
        createChatTemplateBody: data,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateEmployerChatTemplate = createAsyncThunk(
  'update/employer-chat-template',
  async (
    {
      orgId,
      templateId,
      data,
    }: { orgId: string; templateId: string; data: CreateChatTemplateBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiUpdateChatTemplateById({
        chatTemplateId: templateId,
        orgId,
        createChatTemplateBody: data,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteEmployerChatTemplate = createAsyncThunk(
  'delete/employer-chat-template',
  async (
    { templateId, orgId }: { orgId: string; templateId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getOrgApi()
      ).organizationApiDeleteChatTemplateById({
        chatTemplateId: templateId,
        orgId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchPostedJobDetails = createAsyncThunk(
  'get/posted-job-details-{job-id}',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobByIdForEmployer({ jobId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const downloadChatAttachment = createAsyncThunk(
  'download/chat-attachment',
  async ({ singnedS3Url }: { singnedS3Url: string }, { rejectWithValue }) => {
    try {
      window.open(singnedS3Url, '_blank');
      // const response = await axios.get(singnedS3Url, { responseType: 'blob' });
      // const blobUrl = URL.createObjectURL(new Blob([response.data]));
      // const anchor = document.createElement('a');
      // anchor.href = blobUrl;
      // anchor.download = getFilenameFromUrl(singnedS3Url);
      // document.body.appendChild(anchor);
      // anchor.click();
      // URL.revokeObjectURL(blobUrl);
      // document.body.removeChild(anchor);
      return { success: true, message: 'file downloaded successfully' };
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const addChatReaction = createAsyncThunk(
  'post/chat-reaction',
  async (
    {
      chatId,
      jobId,
      data,
    }: { chatId: string; jobId: string; data: AddReactionMessageBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiAddReactionMsg({ chatId, jobId, addReactionMessageBody: data });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateChatReaction = createAsyncThunk(
  'put/chat-reaction',
  async (
    {
      chatId,
      reactionId,
      jobId,
      reaction,
    }: {
      chatId: string;
      reactionId: string;
      jobId: string;
      reaction: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateReactionMsg({
        chatId,
        chatReactionId: reactionId,
        jobId,
        message: reaction,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteChatReaction = createAsyncThunk(
  'delete/chat-reaction',
  async (
    {
      chatId,
      reactionId,
      jobId,
    }: {
      chatId: string;
      reactionId: string;
      jobId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteReactionMsg({ chatId, chatReactionId: reactionId, jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateJobChat = createAsyncThunk(
  'update/message',
  async (
    {
      chatId,
      jobId,
      data,
    }: {
      chatId: string;
      jobId: string;
      data: AddApplicantMessageBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiUpdateJobApplicantMsg({
        chatId,
        jobId,
        addApplicantMessageBody: data,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const deleteJobChat = createAsyncThunk(
  'delete/message',
  async (
    {
      chatId,
      jobId,
    }: {
      chatId: string;
      jobId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiDeleteJobApplicantMsg({ chatId, jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const rejectJobInvite = createAsyncThunk(
  'post/reject-job-invite',
  async (
    {
      jobId,
      userId,
      payload,
    }: { jobId: string; userId: string; payload: JobInviteRejectBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiRejectJobInvitation({
        jobId,
        userId,
        jobInviteRejectBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const chatBlockUnblock = createAsyncThunk(
  'post/chat-block-unblock',
  async (
    {
      isBlocked,
      blockedReason,
      applicantId,
      jobId,
    }: {
      isBlocked: boolean;
      blockedReason: string;
      applicantId: string | undefined;
      jobId: string | undefined;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendBlockChatStatus({
        chatBlockStatusBody: {
          is_blocked: isBlocked,
          blocked_reason: blockedReason,
          applicant_id: applicantId,
          job_id: jobId,
        },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getChatMessages = createAsyncThunk(
  'get/chat-messages',
  async (
    {
      chatGroupId,
      supportChatId,
      jobId,
      orgId,
      pageSize = 20,
      cursor,
      userId,
      roomId,
    }: {
      chatGroupId?: string;
      cursor?: string;
      jobId?: string;
      orgId?: string;
      pageSize?: number;
      roomId?: string;
      supportChatId?: string;
      userId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetChatMessage({
        chatGroupId,
        cursor,
        gameId: undefined,
        jobId,
        orgId,
        pageSize,
        roomId,
        supportChatId,
        userId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const initiateSupportChat = createAsyncThunk(
  'post/support-chat',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiInitiateChatSupport({ orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const initiateAdminSupportChat = createAsyncThunk(
  'post/admin-support-chat',
  async (
    {
      employerId,
      orgId,
      adminInitiateSupportBody,
    }: {
      employerId: string;
      orgId: string;
      adminInitiateSupportBody: AdminInitiateSupportBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiAdminInitiateChatSupport({
        employerId,
        orgId,
        adminInitiateSupportBody,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const initiateBulkAdminSupportChat = createAsyncThunk(
  'post/bulk-admin-support-chat',
  async (
    bulkTemplateSupportBody: BulkTemplateSupportBody,
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendBulkTemplateMessagesByAdmin({ bulkTemplateSupportBody });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

/** An org's support threads, or with no org (faculty) the account's own. */
export const getSupportMessages = createAsyncThunk(
  'get/support-messages',
  async (
    {
      orgId,
      pageNo = 1,
      pageSize = 20,
    }: {
      orgId?: string;
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const chatApi = await apiClient.getChatApi();
      const response = orgId
        ? await chatApi.chatApiGetSupportChat({ orgId, pageNo, pageSize })
        : await chatApi.chatApiGetAccountSupportChat({ pageNo, pageSize });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendChatMessage = createAsyncThunk(
  'post/chat-message',
  async (
    {
      body,
    }: {
      body: ChatMessageBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendChatMessage({ chatMessageBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendAdminChatMessage = createAsyncThunk(
  'post/admin-chat-message',
  async (
    {
      body,
    }: {
      body: ChatMessageBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendChatMessage({ chatMessageBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminSupportMessages = createAsyncThunk(
  'get/admin-support-messages',
  async (
    {
      isResolved,
      pageNo = 1,
      pageSize = 20,
      supportChatId,
      companyName = undefined,
    }: {
      isResolved?: boolean;
      pageNo?: number;
      pageSize?: number;
      supportChatId?: string;
      showLoader?: boolean;
      companyName?: string;
      selectedSupportId: string | null;
      fallbackToFirst: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetAdminSupportChatMessage({
        companyName,
        isResolved,
        pageNo,
        pageSize,
        supportChatId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminChatMessages = createAsyncThunk(
  'get/admin-chat-messages',
  async (
    {
      chatGroupId,
      supportChatId,
      jobId,
      orgId,
      cursor,
      pageSize = 25,
      userId,
      roomId,
    }: {
      chatGroupId?: string;
      cursor?: string;
      jobId?: string;
      orgId?: string;
      pageSize?: number;
      roomId?: string;
      supportChatId?: string;
      userId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetChatMessage({
        chatGroupId,
        cursor,
        gameId: undefined,
        jobId,
        orgId,
        pageSize,
        roomId,
        supportChatId,
        userId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const resolveSupportChat = createAsyncThunk(
  'put/resolve-support-chat',
  async (supportId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiResolveSupportChat({ supportId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getAdminChatStats = createAsyncThunk(
  'get/admin-chat-stats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetAdminSupportCount();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getEmployerDetails = createAsyncThunk(
  'get/employer-details',
  async (
    {
      companyName,
      email,
      employerId,
      name,
      pageNo = 1,
      pageSize = 20,
    }: {
      companyName?: string;
      email?: string;
      employerId?: string;
      name?: string;
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetEmployerList({
        companyName,
        email,
        employerId,
        name,
        pageNo,
        pageSize,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const createChatGroup = createAsyncThunk(
  'post/chat-group',
  async (
    {
      orgId,
      createChatGroupBody,
    }: { orgId: string; createChatGroupBody: CreateChatGroupBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiCreateChatGroup({ orgId, createChatGroupBody });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getChatGroups = createAsyncThunk(
  'get/chat-groups',
  async (
    {
      pageNo = 1,
      pageSize = 20,
    }: {
      pageNo?: number;
      pageSize?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetChatGroup({ pageNo, pageSize });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getFrontChatList = createAsyncThunk(
  'get/front-chat-list',
  async (
    {
      orgId,
      pageNo = 1,
      pageSize = 20,
      searchQuery,
    }: {
      orgId: string;
      pageNo?: number;
      pageSize?: number;
      searchQuery?: string;
      showLoader?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetFrontChatList({ orgId, pageNo, pageSize, searchQuery });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateGroupDetails = createAsyncThunk(
  'put/group-details',
  async (
    {
      orgId,
      groupId,
      updateChatGroupBody,
    }: {
      orgId: string;
      groupId: string;
      updateChatGroupBody: UpdateChatGroupBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiUpdateChatGroup({ groupId, orgId, updateChatGroupBody });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGroupMembers = createAsyncThunk(
  'get/group-members',
  async (groupId: string, { rejectWithValue }) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetChatGroupUsers({ groupId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendGroupChat = createAsyncThunk(
  'post/group-chat',
  async (
    {
      body,
    }: {
      body: ChatMessageBody;
      userName?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendChatMessage({ chatMessageBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const initiateDirectChat = createAsyncThunk(
  'post/initiate-direct-chat',
  async (
    {
      body,
    }: {
      body: ChatMessageBody;
      userDetails: GetOrganizationUsersListResponse;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiSendChatMessage({ chatMessageBody: body });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchGroupChatItems = createAsyncThunk(
  'get/group-chat-items',
  async (
    {
      chatGroupId,
      jobId,
      orgId,
      pageSize = 20,
      supportChatId,
      cursor,
      userId,
      roomId,
    }: {
      chatGroupId?: string;
      cursor?: string;
      jobId?: string;
      orgId?: string;
      pageSize?: number;
      roomId?: string;
      supportChatId?: string;
      userId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiGetChatMessage({
        chatGroupId,
        cursor,
        gameId: undefined,
        jobId,
        orgId,
        pageSize,
        roomId,
        supportChatId,
        userId,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const exitGroup = createAsyncThunk(
  'put/exit-group',
  async (
    {
      groupId,
    }: {
      groupId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiExitChatGroup({ groupId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const assignAdmin = createAsyncThunk(
  'put/assign-group-admin',
  async (
    {
      groupId,
      userId,
    }: {
      groupId: string;
      userId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiAssignChatGroupAdmin({ groupId, memberUserId: userId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const unassignAdmin = createAsyncThunk(
  'put/unassign-group-admin',
  async (
    {
      groupId,
      userId,
    }: {
      groupId: string;
      userId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiUnassignChatGroupAdmin({ groupId, memberUserId: userId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

/** With no org (faculty), only the account's support replies count. */
export const fetchTeamSupportUnreadMessageCount = createAsyncThunk(
  'get/unread-message-count',
  async ({ orgId }: { orgId?: string }, { rejectWithValue }) => {
    try {
      const chatApi = await apiClient.getChatApi();
      const response = orgId
        ? await chatApi.chatApiGetChatCount({ orgId })
        : await chatApi.chatApiGetAccountSupportCount();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getOrgJobsUnreadMessageCount = createAsyncThunk(
  'get/unread-org-jobs-message-count',
  async ({ orgId }: { orgId: string }, { rejectWithValue, signal }) => {
    try {
      const response = await (
        await apiClient.getJobApi()
      ).jobApiGetJobUnreadMessageCount(
        { orgId },
        {
          signal,
        },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteSupportTeamChat = createAsyncThunk(
  'delete/support-team-chat',
  async (
    {
      chatId,
      type,
    }: { chatId: string; type: 'support' | 'team' | 'adminSupport' },
    { rejectWithValue, fulfillWithValue },
  ) => {
    try {
      await (await apiClient.getChatApi()).chatApiDeleteChatMsg({ chatId });
      return fulfillWithValue({ chatId, type });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateSupportOrTeamChat = createAsyncThunk(
  'update/support-or-team-chat',
  async (
    {
      chatId,
      payload,
    }: {
      chatId: string;
      payload: UpdateChatMessageBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await apiClient.getChatApi()
      ).chatApiUpdateChatMsg({ chatId, updateChatMessageBody: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const markJobChatSeen = createAsyncThunk(
  'post/mark-job-chat-seen',
  async (
    { applicantUserId, jobId }: { applicantUserId: string; jobId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await (
        await apiClient.getJobApi()
      ).jobApiJobApplicantChatMarkSeen({
        applicantId: applicantUserId,
        jobId,
      });
      return res.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const markSupportTeamChatSeen = createAsyncThunk(
  'post/mark-team-support-chat-seen',
  async (
    {
      chatIds,
      type,
    }: { chatIds: string[]; type: 'support' | 'team' | 'adminSupport' },
    { rejectWithValue, fulfillWithValue },
  ) => {
    try {
      await (
        await apiClient.getChatApi()
      ).chatApiMarkChatSeen({
        markSeenChatBody: { chat_ids: chatIds },
      });
      return fulfillWithValue({ type });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
